import { NamedSlot } from '../../SimpleTeaser/src/SimpleTeaser';

/**
 * Attempts to parse the value of the given `attribute` of the given `element` as a JSON object.
 * @param {Element} element The target element
 * @param {string} attribute The name of the attribute to parse
 * @returns {any} the parsed object, or `undefined` if the attribute does not exist on the element
 * @throws If the attribute's value is not a valid JSON string
 **/
export const parseAttribute = function(element: Element, attribute: string): any {
  const rawAttribute = element.getAttribute(attribute);
  let parsedAttribute;
  if (rawAttribute) {
    parsedAttribute = JSON.parse(rawAttribute);
  }
  return parsedAttribute;
};

/**
 * Sets the value of the given `attribute` to the given `element`, if no `payload`
 * is given, an empty payload is set
 * @param {Element} element
 * @param {string} attribute
 * @param {{}} payload - defaults to empty object
 */
export const safeSet = function(element: Element, attribute: string, payload = {}): void {
  element.setAttribute(attribute, JSON.stringify(payload));
};

/**
 * Merges data from a give element's attribute to another element's supplied attribute
 * @param {Element} toElement
 * @param {Element} fromElement
 * @param {string} fromAttribute - defaults to 'slot-component-data'
 * @param {string} toAttribute - defaults to 'datajson'
 * @throws If either of the attribute values is not a valid json string
 */
export const mergeData = function(
  fromElement: Element,
  toElement: Element,
  fromAttribute = 'slot-component-data',
  toAttribute = 'datajson',
): void {
  const childData = parseAttribute(fromElement, fromAttribute);
  const parentData = parseAttribute(toElement, toAttribute);
  // TODO smart merge for different types
  // only need to merge if slotContainer has some data
  if (childData) {
    const newData = {
      ...childData,
      ...parentData,
    };
    safeSet(toElement, toAttribute, newData);
  } else {
    safeSet(toElement, toAttribute, parentData);
  }
};

/**
 * This method is used to retrieve attributes from the slotContainer
 * inside a vue <template> and pass them onto the inner web components.
 * @param {HTMLSlotElement} slot
 * @param {Element} slotContainer
 */
export const passDataToSlot = function(slot: HTMLSlotElement, slotContainer: Element) {
  const elements = slot.assignedElements();
  elements.forEach(element => {
    mergeData(slotContainer, element);
  });
};

export const filterVisibleSlots = (namedSlots: any): Array<NamedSlot> => {
  if (namedSlots) {
    const defaultitemsToShow = [] as Array<NamedSlot>;

    Object.keys(namedSlots)
      .filter(el => el !== 'default' && el !== 'validation-box')
      .forEach(key => {
        defaultitemsToShow.push({
          name: key,
          isVisible: !(namedSlots[key] ?? [])[0].data?.attrs?.class?.includes('aem--is-hidden'),
        });
      });
    return defaultitemsToShow;
  }
  return [];
};
